import type {
  AgentCard,
  AgentCardAction,
  AgentCardChecklistItem,
  AgentCardMilestone,
  AgentCardSection,
  AgentCardVerificationCheck,
} from "@opencloud/agent-protocol";
import {
  AlertOctagon,
  AlertTriangle,
  Check,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHelp,
  CircleSlash,
  ExternalLink,
  HeartPulse,
  LockKeyhole,
  Pause,
  PlugZap,
  Rocket,
  ShieldAlert,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
} from "@opencloud/ui";
import { useEffect, useState, type ReactNode } from "react";
import { Markdown } from "./Markdown.js";

interface AgentCardRendererProps {
  readonly card: AgentCard;
  readonly onAction?: (action: AgentCardAction) => void;
  readonly onChoice?: (sectionId: string, optionId: string) => void;
  readonly onCustomAnswer?: (answer: string) => void;
  readonly collapsible?: boolean;
  readonly collapsed?: boolean;
  readonly onToggleCollapse?: () => void;
}

export function AgentCardRenderer({
  card,
  onAction,
  onChoice,
  onCustomAnswer,
  collapsible = false,
  collapsed = false,
  onToggleCollapse,
}: AgentCardRendererProps) {
  return (
    <Card
      className="a3-agent-card"
      data-card-type={card.type}
      data-card-id={card.id}
      data-color={card.styleColor}
      data-state={card.state}
      data-revision={card.revision}
      data-collapsed={collapsed}
    >
      <header
        className="a3-agent-card-header"
        data-collapse-launcher={collapsible}
      >
        {collapsible ? (
          <Button
            type="button"
            variant="ghost"
            className="a3-agent-card-header-toggle"
            aria-expanded={!collapsed}
            aria-label={
              collapsed ? `Expand ${card.title}` : `Collapse ${card.title}`
            }
            title={collapsed ? "Expand card" : "Collapse card"}
            onClick={onToggleCollapse}
          />
        ) : null}
        <span className="a3-agent-card-icon" aria-hidden="true">
          {card.state === "live" ? (
            <Spinner aria-label={`${card.title} in progress`} />
          ) : (
            cardIcon(card.icon)
          )}
        </span>
        <div>
          <p className="a3-agent-card-eyebrow">
            {card.subtitle}
          </p>
          <h2>{card.title}</h2>
        </div>
        {collapsible ? (
          <span className="a3-agent-card-collapse" aria-hidden="true">
            {collapsed ? (
              <ChevronsUpDown aria-hidden="true" />
            ) : (
              <ChevronsDownUp aria-hidden="true" />
            )}
          </span>
        ) : null}
      </header>
      {!collapsed ? (
        <div className="a3-agent-card-content">
          {card.sections.map((section) => (
            <SectionRenderer
              key={section.id}
              section={section}
              onAction={onAction}
              onChoice={onChoice}
              onCustomAnswer={onCustomAnswer}
            />
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function SectionRenderer({
  section,
  onAction,
  onChoice,
  onCustomAnswer,
}: {
  readonly section: AgentCardSection;
  readonly onAction?: AgentCardRendererProps["onAction"];
  readonly onChoice?: AgentCardRendererProps["onChoice"];
  readonly onCustomAnswer?: AgentCardRendererProps["onCustomAnswer"];
}) {
  switch (section.type) {
    case "markdown":
      return (
        <section
          className={`a3-agent-card-markdown${section.id === "latest" ? " a3-agent-card-latest" : ""}`}
          data-display={section.display}
          data-color={section.styleColor ?? "neutral"}
        >
          {section.display === "box" && section.icon ? (
            <span className="a3-agent-card-box-icon" aria-hidden="true">
              {cardIcon(section.icon)}
            </span>
          ) : null}
          <div>
            {section.title || section.meta || section.occurredAt ? (
              <div className="a3-agent-card-section-head">
                {section.title ? <strong>{section.title}</strong> : <span />}
                {section.occurredAt ? (
                  <CardTime occurredAt={section.occurredAt} />
                ) : section.meta ? (
                  <small>{section.meta}</small>
                ) : null}
              </div>
            ) : null}
            <Markdown>{section.markdown}</Markdown>
          </div>
        </section>
      );
    case "checklist":
      return (
        <section className="a3-agent-card-section">
          {section.title ? (
            <p className="a3-agent-card-section-label">{section.title}</p>
          ) : null}
          <ol className="a3-agent-card-checklist">
            {section.items.map((item) => (
              <ChecklistRow key={item.id} item={item} />
            ))}
          </ol>
        </section>
      );
    case "milestones":
      return (
        <section className="a3-agent-card-section">
          {section.title ? (
            <p className="a3-agent-card-section-label">{section.title}</p>
          ) : null}
          <ol className="a3-agent-card-milestones">
            {section.items.map((milestone) => (
              <MilestoneRow key={milestone.id} milestone={milestone} />
            ))}
          </ol>
        </section>
      );
    case "choices":
      return (
        <Choices
          section={section}
          onChoice={onChoice}
          onCustomAnswer={onCustomAnswer}
        />
      );
    case "list":
      return (
        <section className="a3-agent-card-section">
          {section.title ? (
            <p className="a3-agent-card-section-label">{section.title}</p>
          ) : null}
          <ul className="a3-agent-card-list">
            {section.items.map((item) => (
              <li key={item.id}>
                {item.icon === "pause" ? (
                  <Pause aria-hidden="true" />
                ) : item.icon === "lock" ? (
                  <LockKeyhole aria-hidden="true" />
                ) : (
                  <Check aria-hidden="true" />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </section>
      );
    case "verification":
      return (
        <section className="a3-agent-card-section">
          {section.title ? (
            <p className="a3-agent-card-section-label">{section.title}</p>
          ) : null}
          <div className="a3-agent-card-checks">
            {section.checks.map((check) => (
              <VerificationRow key={check.id} check={check} />
            ))}
          </div>
        </section>
      );
    case "actions":
      return (
        <div className="a3-agent-card-actions">
          {section.actions.map((action) => (
            <Action key={action.id} action={action} onAction={onAction} />
          ))}
        </div>
      );
    case "stats":
      return (
        <div className="a3-agent-card-stats">
          {section.items.map((item) => (
            <span key={item.id}>
              <strong>{item.value}</strong> {item.label}
            </span>
          ))}
        </div>
      );
    case "disclosure":
      return (
        <Disclosure
          section={section}
          onAction={onAction}
          onChoice={onChoice}
          onCustomAnswer={onCustomAnswer}
        />
      );
  }
}

function MilestoneRow({
  milestone,
}: {
  readonly milestone: AgentCardMilestone;
}) {
  const [expanded, setExpanded] = useState(milestone.state === "active");
  useEffect(() => {
    setExpanded(milestone.state === "active");
  }, [milestone.state]);
  const stateLabel =
    milestone.state === "completed"
      ? "Completed"
      : milestone.state === "active"
        ? "In progress"
        : milestone.state === "blocked"
          ? "Blocked"
          : "Not started";
  return (
    <li data-state={milestone.state} data-expanded={expanded}>
      <span className="a3-agent-card-milestone-rail" aria-hidden="true">
        <span>
          {milestone.state === "completed" ? (
            <Check />
          ) : milestone.state === "active" ? (
            <Spinner aria-label={`${milestone.title} in progress`} />
          ) : milestone.state === "blocked" ? (
            <AlertTriangle />
          ) : (
            <Circle />
          )}
        </span>
      </span>
      <div className="a3-agent-card-milestone-copy">
        <Button
          type="button"
          variant="ghost"
          className="a3-agent-card-milestone-head"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <strong>{milestone.title}</strong>
          <span>
            <em>{stateLabel}</em>
            {expanded ? (
              <ChevronDown aria-hidden="true" />
            ) : (
              <ChevronRight aria-hidden="true" />
            )}
          </span>
        </Button>
        {expanded ? (
          <div className="a3-agent-card-milestone-detail">
            {milestone.detail ? <p>{milestone.detail}</p> : null}
            <ul className="a3-agent-card-submilestones">
              {milestone.items.map((item) => (
                <ChecklistRow key={item.id} item={item} compact />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function ChecklistRow({
  item,
  compact = false,
}: {
  readonly item: AgentCardChecklistItem;
  readonly compact?: boolean;
}) {
  return (
    <li data-state={item.state} data-compact={compact}>
      <span className="a3-agent-card-checklist-icon" aria-hidden="true">
        {item.state === "completed" ? (
          <Check />
        ) : item.state === "active" ? (
          <Spinner aria-label={`${item.title} in progress`} />
        ) : item.state === "blocked" ? (
          <AlertTriangle />
        ) : (
          <Circle />
        )}
      </span>
      <span>
        <strong>{item.title}</strong>
        {item.detail ? <small>{item.detail}</small> : null}
      </span>
    </li>
  );
}

function CardTime({ occurredAt }: { readonly occurredAt: string }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(interval);
  }, []);
  return (
    <small title={new Date(occurredAt).toLocaleString()}>
      {formatCardTimestamp(occurredAt, now)}
    </small>
  );
}

export function formatCardTimestamp(
  occurredAt: string,
  now = new Date(),
): string {
  const occurred = new Date(occurredAt);
  if (!Number.isFinite(occurred.getTime())) return "";
  const elapsed = Math.max(0, now.getTime() - occurred.getTime());
  if (elapsed < 60_000) return "now";
  if (elapsed < 60 * 60_000) return `${Math.floor(elapsed / 60_000)}m ago`;
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(occurred);
}

function Choices({
  section,
  onChoice,
  onCustomAnswer,
}: {
  readonly section: Extract<AgentCardSection, { type: "choices" }>;
  readonly onChoice?: AgentCardRendererProps["onChoice"];
  readonly onCustomAnswer?: AgentCardRendererProps["onCustomAnswer"];
}) {
  const [reply, setReply] = useState("");
  const [selected, setSelected] = useState("");
  return (
    <section className="a3-agent-card-choices">
      <RadioGroup
        aria-label="Choose how the agent should continue"
        value={selected}
        onValueChange={(value) => {
          const optionId = String(value);
          setSelected(optionId);
          onChoice?.(section.id, optionId);
        }}
      >
        {section.options.map((option) => (
        <Label
          key={option.id}
          className="a3-agent-card-choice"
          data-recommended={option.recommended ?? false}
          data-selected={selected === option.id || undefined}
        >
          <span className="a3-agent-card-choice-box">
            <RadioGroupItem value={option.id} aria-label={option.label} />
          </span>
          <span className="a3-agent-card-choice-copy">
            <span>
              <strong>{option.label}</strong>
              {option.recommended ? <em>Recommended</em> : null}
            </span>
            <small>{option.detail}</small>
          </span>
        </Label>
        ))}
      </RadioGroup>
      {section.customAnswer ? (
        <div className="a3-agent-card-choice a3-agent-card-custom-answer">
          <div className="a3-agent-card-choice-copy">
            <label htmlFor={`${section.id}-custom-answer`}>
              {section.customAnswer.label}
            </label>
            <div className="a3-agent-card-custom-row">
              <Input
                id={`${section.id}-custom-answer`}
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder={section.customAnswer.placeholder}
              />
              <Button
                type="button"
                disabled={!reply.trim()}
                onClick={() => {
                  const answer = reply.trim();
                  if (!answer) return;
                  onCustomAnswer?.(answer);
                  setReply("");
                }}
              >
                {section.customAnswer.actionLabel}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function Action({
  action,
  onAction,
}: {
  readonly action: AgentCardAction;
  readonly onAction?: AgentCardRendererProps["onAction"];
}) {
  const className = `a3-agent-card-action a3-agent-card-action-${action.style}`;
  if (action.href) {
    return (
      <Button
        render={<a href={action.href} target="_blank" rel="noreferrer noopener" />}
        className={className}
        variant={buttonVariant(action.style)}
      >
        {action.action === "visit_app" ? (
          <ExternalLink aria-hidden="true" />
        ) : null}
        {action.label}
      </Button>
    );
  }
  return (
    <Button
      type="button"
      className={className}
      variant={buttonVariant(action.style)}
      onClick={() => onAction?.(action)}
    >
      {action.label}
    </Button>
  );
}

function Disclosure({
  section,
  onAction,
  onChoice,
  onCustomAnswer,
}: {
  readonly section: Extract<AgentCardSection, { type: "disclosure" }>;
  readonly onAction?: AgentCardRendererProps["onAction"];
  readonly onChoice?: AgentCardRendererProps["onChoice"];
  readonly onCustomAnswer?: AgentCardRendererProps["onCustomAnswer"];
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <section className="a3-agent-card-disclosure">
      <Button
        type="button"
        className="a3-agent-card-action a3-agent-card-action-secondary"
        variant="outline"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <ChevronRight
          className="a3-agent-card-chevron"
          data-expanded={expanded}
          aria-hidden="true"
        />
        {expanded ? (section.expandedLabel ?? "Hide details") : section.label}
      </Button>
      {expanded ? (
        <div className="a3-agent-card-disclosure-content">
          {section.sections.map((nested) => (
            <SectionRenderer
              key={nested.id}
              section={nested}
              onAction={onAction}
              onChoice={onChoice}
              onCustomAnswer={onCustomAnswer}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function VerificationRow({
  check,
}: {
  readonly check: AgentCardVerificationCheck;
}) {
  const label =
    check.status === "passed"
      ? "Passed"
      : check.status === "failed"
        ? "Failed"
        : check.status === "not_needed"
          ? "Not needed"
          : "Not checked";
  return (
    <div className="a3-agent-card-verification" data-status={check.status}>
      <span aria-hidden="true">
        {check.status === "passed" ? (
          <Check />
        ) : check.status === "failed" ? (
          <X />
        ) : (
          <CircleSlash />
        )}
      </span>
      <span>
        <strong>{check.label}</strong>
        {check.detail ? <small>{check.detail}</small> : null}
      </span>
      <em>{label}</em>
    </div>
  );
}

function cardIcon(icon: AgentCard["icon"]): ReactNode {
  switch (icon) {
    case "loader":
      return <Spinner aria-label="In progress" />;
    case "question":
      return <CircleHelp />;
    case "permission":
      return <ShieldAlert />;
    case "connection":
      return <PlugZap />;
    case "rocket":
      return <Rocket />;
    case "check":
      return <Check />;
    case "alert":
      return <AlertOctagon />;
    case "stopped":
      return <CircleSlash />;
    case "health":
      return <HeartPulse />;
    case "lock":
      return <LockKeyhole />;
  }
}

function buttonVariant(style: AgentCardAction["style"]) {
  if (style === "destructive") return "destructive" as const;
  if (style === "secondary") return "outline" as const;
  return "default" as const;
}
